import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "../../../elements/V2/Icons/ArrowIcon"
import Typography from "../../../elements/V2/Typography"
import LayoutItem from "../../Layouts/LayoutItem"

import React from "react"

type BlogProps = {
  overrideTextColor: Color
  imageUrl: string
  imageAlt: string
  title: string
  body?: string
  date?: Date
  ctaText?: string
  ctaUrl?: string | YocoCom.YocoLink
  ctaColor?: Color
  superscriptText?: string
  footerTitle?: string
  footerSubtitle?: string
  mobileGridOrder?: number
  isSliderChild?: boolean
}

const Blog = ({
  overrideTextColor,
  imageUrl,
  imageAlt,
  title,
  body,
  date,
  ctaText,
  ctaUrl,
  ctaColor,
  superscriptText,
  footerTitle,
  footerSubtitle,
  mobileGridOrder,
  isSliderChild,
  ...props
}: BlogProps) => {
  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      isSliderChild={isSliderChild}
      className={classNames(
        "col-span-full flex flex-col gap-20 sm:col-span-2 md:col-span-3 md:gap-24 lg:col-span-4 lg:gap-32"
      )}
      {...props}
    >
      <GatsbyStoryblokImage
        className="w-auto flex-none"
        aspectRatio="4:3"
        image={imageUrl}
        loadType="eager"
        alt={imageAlt || "Blog image"}
        rounded
      />

      <div className="flex h-full grow flex-col gap-16 md:gap-20 lg:gap-24">
        <div className="flex grow flex-col gap-8">
          {superscriptText ? (
            <Typography
              className="opacity-50"
              color={overrideTextColor}
              weight="book"
              font="grotesk"
              size="subscript-lg"
              text={superscriptText}
            />
          ) : null}

          <Typography
            color={overrideTextColor}
            weight="medium"
            font="grotesk"
            size="lead-md"
            text={title}
          />

          {body && (
            <Typography
              color={overrideTextColor}
              weight="book"
              font="grotesk"
              size="body-lg"
              text={body}
            />
          )}
        </div>

        <div className="flex flex-col gap-8">
          {footerTitle || footerSubtitle ? (
            <div className="mt-20">
              {footerTitle ? (
                <Typography
                  color={overrideTextColor}
                  weight="medium"
                  font="grotesk"
                  size="body-lg"
                  text={footerTitle}
                />
              ) : null}

              {footerSubtitle ? (
                <Typography
                  color={overrideTextColor}
                  weight="book"
                  font="grotesk"
                  size="body-lg"
                  text={footerSubtitle}
                />
              ) : null}
            </div>
          ) : null}

          {date && (
            <Typography
              className="uppercase tracking-[0.8px] opacity-50"
              color={overrideTextColor}
              weight="book"
              font="grotesk"
              size="subscript-md"
              text={new Date(date).toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            />
          )}
        </div>

        {ctaText && ctaUrl && (
          <TextButton
            style="icon"
            text={ctaText}
            icon={<ArrowIcon />}
            linkUrl={ctaUrl}
            className="flex-none"
            color={ctaColor ? ctaColor : overrideTextColor}
          />
        )}
      </div>
    </LayoutItem>
  )
}

export default Blog
